/**
 * Hebrew copy for a row in the in-app notification bell (NotificationsDialog).
 *
 * Extracted from the component so the tests exercise the REAL strings. The test
 * file previously kept its own near-identical copy, which passed happily while
 * the component drifted — the same decoy pattern that once hid a bug in the push
 * URL resolver.
 *
 * A row whose `type` has no case here renders as the generic 'התראה חדשה', so
 * any new notification type needs a case here AND a route in
 * notificationDropdownNav.ts, or it lands in the bell as an untappable mystery.
 *
 * Gender-neutral by construction: "<name>: <what happened>" (like a chat
 * preview) instead of a past-tense verb, which in Hebrew carries the actor's
 * gender — "דנה אהב את הפוסט שלך" was wrong for most of the members.
 */
export function getNotificationText(notification: Record<string, any>): string {
  // trim: some display names end in a space, which left "דנה : לייק…".
  const actorName = notification.actor?.display_name?.trim() || 'משתמש';
  const mentionedBy = notification.mentioned_by?.display_name?.trim() || 'משתמש';
  const eventTitle = notification.event?.title || 'אירוע';

  switch (notification.type) {
    case 'like':
      return `${actorName}: לייק ${notification.post_id ? 'לפוסט' : 'לתגובה'} שלך`;
    case 'comment':
      return `${actorName}: תגובה חדשה לפוסט שלך`;
    case 'reply':
      return `${actorName}: תשובה לתגובה שלך`;
    case 'share':
      return `${actorName}: שיתוף של הפוסט שלך`;
    case 'new_event':
      return `אירוע חדש: ${eventTitle}`;
    case 'event_reminder':
      return `תזכורת: ${eventTitle} מתחיל בקרוב`;
    case 'follow':
      return `${actorName}: מעקב חדש אחריך`;
    case 'mention':
      return `תויגת בפוסט על ידי ${actorName}`;
    case 'new_message':
      return `${actorName}: הודעה חדשה`;
    case 'new_course_content':
      return 'שיעור חדש זמין 📚 — הסרטון היומי שלכם מחכה — בואו לצפות';
    case 'comment_reply':
      return `${actorName}: תשובה לתגובה שלך בשיעור`;
    case 'referral_reward_given':
      return 'קיבלתם חודש מתנה על חבר/ה שהזמנתם 🎁';
    case 'referral_reward_claimable':
      return 'חודש מתנה מחכה לכם — לחצו כדי להפעיל';
    default:
      if (notification.mentioned_by_user_id) {
        return `תויגת בפוסט על ידי ${mentionedBy}`;
      }
      return 'התראה חדשה';
  }
}
